// Cloud Sync Service
// Handles merging local progress with Firestore when user signs in.

import { auth, db, isFirebaseConfigured } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const COLLECTION = 'users';

// ─── Public API ────────────────────────────────────────────

/**
 * Returns the current Firebase user or null.
 */
export function currentUser() {
    if (!isFirebaseConfigured || !auth) return null;
    return auth.currentUser;
}

/**
 * Called right after a successful sign-in.
 * Merges local data with whatever is in Firestore and writes the merged
 * result to both Firestore and localStorage.
 */
export async function syncOnLogin() {
    if (!isFirebaseConfigured || !auth) return null;
    const user = auth.currentUser;
    if (!user) return null;

    const localProgress  = getLocalProgress();
    const localVision    = getLocalVisionTests();
    const localAvatar    = localStorage.getItem('eyesTrainer_avatar') || 'dog';
    const localNickname  = localStorage.getItem('eyesTrainer_nickname') || '';
    const localTheme       = localStorage.getItem('eyesTrainer_theme') || 'dark';
    const localAudio       = localStorage.getItem('eyesTrainer_audio') || 'false';

    const ref = doc(db, COLLECTION, user.uid);
    const snap = await getDoc(ref);

    let merged;

    if (!snap.exists()) {
        // First time login – push local data to cloud
        merged = {
            nickname: localNickname || user.displayName || '',
            avatar: localAvatar,
            progress: localProgress,
            visionTests: localVision,
            preferences: { theme: localTheme, audio: localAudio },
        };
    } else {
        const cloud = snap.data();
        merged = {
            nickname: localNickname || cloud.nickname || user.displayName || '',
            avatar: localAvatar || cloud.avatar || 'dog',
            progress: mergeProgress(localProgress, cloud.progress || {}),
            visionTests: mergeVisionTests(localVision, cloud.visionTests || []),
            preferences: {
                theme: localTheme || (cloud.preferences && cloud.preferences.theme) || 'dark',
                audio: localAudio || (cloud.preferences && cloud.preferences.audio) || 'false',
            },
        };
    }

    // Write merged to Firestore
    await setDoc(ref, { ...merged, updatedAt: serverTimestamp() }, { merge: true });

    // Write merged back to localStorage
    writeLocalProgress(merged.progress);
    writeLocalVisionTests(merged.visionTests);
    localStorage.setItem('eyesTrainer_avatar', merged.avatar);
    localStorage.setItem('eyesTrainer_nickname', merged.nickname);

    return merged;
}

/**
 * Save current progress to Firestore (called after each session recording).
 */
export async function saveToCloud(progress) {
    if (!isFirebaseConfigured || !auth) return;
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, COLLECTION, user.uid);
    await setDoc(ref, { progress, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Save vision tests to cloud.
 */
export async function saveVisionToCloud(visionTests) {
    if (!isFirebaseConfigured || !auth) return;
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, COLLECTION, user.uid);
    await setDoc(ref, { visionTests, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Save user profile (nickname, avatar) to cloud.
 */
export async function saveProfileToCloud(nickname, avatar) {
    if (!isFirebaseConfigured || !auth) return;
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, COLLECTION, user.uid);
    await setDoc(ref, { nickname, avatar, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── Merge helpers ─────────────────────────────────────────

function mergeProgress(local, cloud) {
    if (!cloud || !cloud.completedDays) return local;
    if (!local || !local.completedDays) return cloud;

    // Merge completedDays – union, take higher count/points per day
    const mergedDays = { ...cloud.completedDays };
    for (const [date, dayData] of Object.entries(local.completedDays)) {
        if (!mergedDays[date]) {
            mergedDays[date] = dayData;
        } else {
            mergedDays[date] = {
                ...mergedDays[date],
                count: Math.max(mergedDays[date].count || 0, dayData.count || 0),
                points: Math.max(mergedDays[date].points || 0, dayData.points || 0),
            };
        }
    }

    // Merge difficultyHistory – take max per difficulty
    const localDH = local.difficultyHistory || {};
    const cloudDH = cloud.difficultyHistory || {};
    const mergedDH = { ...cloudDH };
    for (const [diff, count] of Object.entries(localDH)) {
        mergedDH[diff] = Math.max(mergedDH[diff] || 0, count);
    }

    // Merge badges – union by ID
    const localBadges = local.badges || [];
    const cloudBadges = cloud.badges || [];
    const badgeMap = {};
    for (const b of cloudBadges) badgeMap[b.id] = b;
    for (const b of localBadges) {
        if (!badgeMap[b.id]) badgeMap[b.id] = b;
    }

    return {
        completedDays: mergedDays,
        currentStreak: Math.max(local.currentStreak || 0, cloud.currentStreak || 0),
        longestStreak: Math.max(local.longestStreak || 0, cloud.longestStreak || 0),
        totalSessions: Math.max(local.totalSessions || 0, cloud.totalSessions || 0),
        totalPoints: Math.max(local.totalPoints || 0, cloud.totalPoints || 0),
        badges: Object.values(badgeMap),
        lastCompletedDate: latest(local.lastCompletedDate, cloud.lastCompletedDate),
        firstSessionDate: earliest(local.firstSessionDate, cloud.firstSessionDate),
        difficultyHistory: mergedDH,
    };
}

function mergeVisionTests(local, cloud) {
    // Union by date string (ISO), deduplicate
    const map = {};
    for (const t of cloud) map[t.date] = t;
    for (const t of local) {
        if (!map[t.date]) map[t.date] = t;
    }
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

function latest(a, b) {
    if (!a) return b;
    if (!b) return a;
    return a > b ? a : b;
}

function earliest(a, b) {
    if (!a) return b;
    if (!b) return a;
    return a < b ? a : b;
}

// ─── localStorage read/write helpers ───────────────────────

function getLocalProgress() {
    try {
        const stored = localStorage.getItem('eyeTrainer_progress');
        return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
}

function writeLocalProgress(progress) {
    localStorage.setItem('eyeTrainer_progress', JSON.stringify(progress));
}

function getLocalVisionTests() {
    try {
        const stored = localStorage.getItem('eyeTrainer_visionTests');
        return stored ? JSON.parse(stored) : [];
    } catch { return []; }
}

function writeLocalVisionTests(tests) {
    localStorage.setItem('eyeTrainer_visionTests', JSON.stringify(tests));
}
